import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getAppointmentById } from "service/api";
import Preloader from "components/Preloader/Preloader";

const THEME = "#f46a6a";

const FieldBox = ({ label, value, icon }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: "9px", padding: "8px 12px", background: "#fff", border: "1px solid #f5f0f0", borderRadius: "8px" }}>
    <div style={{ width: "26px", height: "26px", borderRadius: "6px", background: "#fff5f5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "1px" }}>
      <i className={`mdi ${icon}`} style={{ color: THEME, fontSize: "13px" }} />
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: "9px", fontWeight: "700", color: "#bbb", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
      <div style={{ fontSize: "12px", fontWeight: "600", color: "#333", marginTop: "1px", wordBreak: "break-word" }}>
        {value || <span style={{ color: "#ccc", fontWeight: "400" }}>—</span>}
      </div>
    </div>
  </div>
);

const SectionHeader = ({ icon, title }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "7px", marginBottom: "10px", paddingBottom: "7px", borderBottom: `2px solid #fde8e8` }}>
    <i className={`mdi ${icon}`} style={{ color: THEME, fontSize: "16px" }} />
    <span style={{ fontSize: "11px", fontWeight: "800", color: "#444", textTransform: "uppercase", letterSpacing: "0.7px" }}>{title}</span>
  </div>
);

function AppointmentDetails() {
  const { id } = useParams();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAppointmentDetails(); }, [id]);

  const fetchAppointmentDetails = async () => {
    try {
      const res = await getAppointmentById(id);
      if (res?.data?.data) setAppointment(res.data.data);
    } catch (err) {
      console.error("Error fetching appointment details:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Preloader />;
  if (!appointment) return <div className="page-content"><p>Appointment not found.</p></div>;

  const formatDate = (d) => d
    ? new Date(d).toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
    : "—";

  const formatTime12 = (t) => {
    if (!t) return "—";
    const [h, m] = t.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    return `${String(h % 12 || 12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
  };

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #appt-print, #appt-print * { visibility: visible !important; }
          #appt-print { position: fixed; top: 0; left: 0; width: 100%; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="page-content">
        <div className="container-fluid">

          {/* Toolbar */}
          <div className="d-flex justify-content-between align-items-center mb-3 no-print">
            <h5 style={{ margin: 0, fontWeight: "700", color: "#333" }}>Appointment Details</h5>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => window.print()}
                style={{ background: THEME, color: "#fff", border: "none", borderRadius: "7px", padding: "6px 16px", fontWeight: "600", fontSize: "13px", display: "flex", alignItems: "center", gap: "5px", cursor: "pointer" }}
              >
                <i className="mdi mdi-printer" /> Print
              </button>
              <Link to="/AppointmentList" style={{ background: "#f5f5f5", color: "#555", border: "none", borderRadius: "7px", padding: "6px 16px", fontWeight: "600", fontSize: "13px", display: "flex", alignItems: "center", gap: "5px", textDecoration: "none" }}>
                <i className="mdi mdi-arrow-left" /> Back
              </Link>
            </div>
          </div>

          {/* Printable area */}
          <div id="appt-print" style={{ background: "#fff", borderRadius: "12px", overflow: "hidden", border: "1px solid #f0e8e8", boxShadow: "0 3px 16px rgba(244,106,106,0.08)" }}>

            {/* ── Header ── */}
            <div style={{ background: `linear-gradient(135deg, ${THEME} 0%, #e84393 100%)`, padding: "18px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ width: "42px", height: "42px", borderRadius: "10px", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="mdi mdi-hospital-building" style={{ color: "#fff", fontSize: "22px" }} />
                </div>
                <div>
                  <div style={{ fontSize: "16px", fontWeight: "800", color: "#fff" }}>{appointment.clinic_name || "Medical Practice"}</div>
                  <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.75)" }}>Appointment Confirmation</div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: "1px" }}>Ref No.</div>
                  <div style={{ fontSize: "15px", fontWeight: "800", color: "#fff", fontFamily: "monospace", letterSpacing: "1px" }}>{appointment.appointment_no}</div>
                </div>
                <div style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.35)", borderRadius: "20px", padding: "4px 12px", fontSize: "11px", fontWeight: "700", color: "#fff" }}>
                  ● {appointment.appointment_status || "Scheduled"}
                </div>
              </div>
            </div>

            {/* ── Quick Stats Bar ── */}
            <div style={{ background: "#fdf8f8", borderBottom: "1px solid #f5eded", padding: "12px 24px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {[
                { icon: "mdi-calendar-check", label: "Date", value: formatDate(appointment.appointment_date) },
                { icon: "mdi-clock-time-four-outline", label: "Time", value: `${formatTime12(appointment.apnt_start_time)} – ${formatTime12(appointment.apnt_end_time)}` },
                { icon: "mdi-tag-outline", label: "Type", value: appointment.apnt_type || "—" },
                { icon: "mdi-bell-outline", label: "Reminder", value: appointment.apnt_reminder || "—" },
                { icon: "mdi-calendar-plus", label: "Booked On", value: appointment.date ? `${appointment.date} ${appointment.time || ""}`.trim() : "—" },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", background: "#fff", border: "1px solid #f5eded", borderRadius: "8px", padding: "7px 14px", flex: "1 1 150px" }}>
                  <i className={`mdi ${item.icon}`} style={{ color: THEME, fontSize: "16px", flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: "9px", color: "#bbb", textTransform: "uppercase", fontWeight: "700" }}>{item.label}</div>
                    <div style={{ fontSize: "12px", fontWeight: "700", color: "#333" }}>{item.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Body ── */}
            <div style={{ padding: "18px 24px", display: "flex", flexDirection: "column", gap: "18px" }}>

              {/* Patient Section */}
              <div>
                <SectionHeader icon="mdi-account-circle-outline" title="Patient Information" />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "8px" }}>
                  <FieldBox icon="mdi-account" label="Full Name" value={`${appointment.patient_title ? appointment.patient_title + ". " : ""}${appointment.patient_name}`} />
                  <FieldBox icon="mdi-gender-male-female" label="Gender" value={appointment.gender} />
                  <FieldBox icon="mdi-cake-variant-outline" label="Date of Birth"
                    value={appointment.patient_dob ? new Date(appointment.patient_dob).toLocaleDateString("en-GB").replace(/\//g, "-") : null} />
                  <FieldBox icon="mdi-phone-outline" label="Contact No." value={appointment.mobileno} />
                  <FieldBox icon="mdi-email-outline" label="Email" value={appointment.email} />
                  {appointment.patient_mpi
                    ? <FieldBox icon="mdi-card-account-details-outline" label="MPI Number" value={appointment.patient_mpi} />
                    : <>
                        <FieldBox icon="mdi-flag-outline" label="Nationality" value={appointment.app_nationality} />
                        <FieldBox icon="mdi-card-account-details-outline" label="ID Number" value={appointment.patient_idno} />
                      </>
                  }
                </div>
              </div>

              {/* Appointment Section */}
              <div>
                <SectionHeader icon="mdi-clipboard-text-outline" title="Appointment Information" />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "8px" }}>
                  <FieldBox icon="mdi-hospital-building" label="Practice" value={appointment.clinic_name} />
                  <FieldBox icon="mdi-barcode" label="Appointment No." value={appointment.appointment_no} />
                  <FieldBox icon="mdi-calendar" label="Appointment Date" value={formatDate(appointment.appointment_date)} />
                  <FieldBox icon="mdi-clock-start" label="Start Time" value={formatTime12(appointment.apnt_start_time)} />
                  <FieldBox icon="mdi-clock-end" label="End Time" value={formatTime12(appointment.apnt_end_time)} />
                  <FieldBox icon="mdi-clock-outline" label="Slot" value={appointment.appointment_time} />
                  <FieldBox icon="mdi-stethoscope" label="Appointment Type" value={appointment.apnt_type} />
                  <FieldBox icon="mdi-check-decagram-outline" label="Status" value={appointment.appointment_status} />
                  <FieldBox icon="mdi-calendar-plus" label="Booked On" value={appointment.date} />
                  <FieldBox icon="mdi-timer-outline" label="Booked At" value={appointment.time} />
                </div>
              </div>

              {/* Notes */}
              {appointment.notes && (
                <div style={{ background: "#fffbf0", border: "1px solid #fde8b0", borderRadius: "8px", padding: "10px 14px" }}>
                  <div style={{ fontSize: "10px", fontWeight: "700", color: "#b8860b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>
                    <i className="mdi mdi-note-text-outline me-1" />Notes
                  </div>
                  <div style={{ fontSize: "13px", color: "#555" }}>{appointment.notes}</div>
                </div>
              )}
            </div>

            {/* ── Footer ── */}
            <div style={{ background: "#fdf8f8", borderTop: "1px solid #f5eded", padding: "10px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: "11px", color: "#bbb", display: "flex", alignItems: "center", gap: "5px" }}>
                <i className="mdi mdi-shield-check-outline" style={{ color: "#ddd" }} />
                Please arrive 10 minutes before your scheduled appointment time.
              </div>
              <div style={{ fontSize: "10px", color: "#ccc", fontFamily: "monospace" }}>Ref: {appointment.appointment_no}</div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

export default AppointmentDetails;
